import React from "react";
import ReactPlayer from "react-player";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import textSizeBasedOnLength from "../../funcs/textResizer";
import { TeamRail, VoteCount, voteTargetClasses } from "./liveUi";

const Media = ({ q }) => {
  if (q?.questionVideo && q.questionVideo.length > 10) {
    return (
      <ReactPlayer url={q.questionVideo} controls width="100%" height="100%" />
    );
  }
  if (q?.questionAudio && q.questionAudio.length > 10) {
    return (
      <div className="w-full h-full flex flex-col justify-center items-center">
        <div className="w-full max-w-md bg-gray-100 p-6 rounded-xl shadow-md">
          <audio src={q.questionAudio} controls className="w-full" />
        </div>
      </div>
    );
  }
  if (q?.questionImage && q.questionImage.length > 10) {
    return (
      <img
        src={q.questionImage}
        alt="سؤال"
        className="h-full object-contain"
      />
    );
  }
  return <div className="w-full h-full flex justify-center items-center" />;
};

// Read-only mirror of the question screen — frame copied from
// components/tarbaa/Question.js. The reveal button and the rail's powerups
// are the vote targets; active powerups (choices / first letter / answer
// pattern / phone) mirror exactly what the game master's screen shows.
const LiveQuestion = ({ state, presence, voting }) => {
  const q = state?.question;
  if (!q) return null;
  const active = state?.powerUps?.active || {};
  const revealMine = voting.myVote === "reveal";
  const revealCount = voting.counts?.reveal || 0;

  return (
    <div className="w-full h-[90vh] flex flex-col portrait:flex-col sm:flex-row portrait:py-10 pl-10 pr-2 py-10 xl:py-20">
      <div
        className={`portrait:w-full portrait:h-3/4 relative h-3/4 w-full sm:w-5/6 sm:h-full ${textSizeBasedOnLength(
          q.text
        )} font-bold text-dark border-8 border-behindButton rounded-[50px] pt-4 sm:pt-6 md:pt-8`}
      >
        {/* Floating top badges */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-11/12 sm:w-full flex flex-row justify-between items-center bg-transparent">
          {/* Category */}
          <div className="w-1/3 flex justify-center items-center">
            <div
              className={`hidden sm:block border-[#343434] border-4 rounded-2xl ${
                q.isCustom ? "bg-mainText" : ""
              }`}
            >
              {q.isCustom && (
                <div className="text-center px-2 text-dark rounded-lg text-sm lg:text-xl">
                  فئة خاصة
                </div>
              )}
              <div className="text-center p-1 px-4 lg:p-2 bg-[#343434] text-white rounded-lg text-sm lg:text-xl">
                {q.modeName}
              </div>
            </div>
          </div>

          {/* Center — live badge (the stopwatch lives with the game master) */}
          <div className="w-1/3 flex justify-center items-center">
            <div className="p-1 px-4 md:p-3 bg-[#343434] text-white rounded-full flex items-center gap-3 text-sm lg:text-xl">
              <span className="w-2.5 h-2.5 rounded-full bg-danger animate-pulse" />
              <h1 className="text-center">بث مباشر</h1>
            </div>
          </div>

          {/* Points + margin-of-error badge (mirrors the app's chevron pill) */}
          <div className="w-1/3 flex justify-center items-center">
            <div className="hidden sm:flex flex-row items-center">
              {q.marginText && (
                <div className="flex flex-row items-center gap-1 p-1 px-2 bg-[#343434] border-4 border-[#343434] rounded-l-2xl text-white text-sm lg:text-xl">
                  <span className="flex flex-col items-center leading-none">
                    <FaChevronUp className="text-success w-2.5 h-2.5" />
                    <FaChevronDown className="text-danger w-2.5 h-2.5" />
                  </span>
                  <span className="font-bold">{q.marginText}</span>
                </div>
              )}
              <div
                className={`border-[#343434] border-4 ${
                  q.marginText ? "rounded-r-2xl" : "rounded-2xl"
                }`}
              >
                <div className="text-right p-1 px-4 lg:p-2 bg-[#343434] text-white rounded-lg text-sm lg:text-xl flex gap-1">
                  <div>نقطة</div>
                  <div>{q.points}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Question content */}
        <div className="h-2/6 xl:h-1/6 w-full flex flex-col justify-center items-center p-2 px-4 lg:px-10">
          <div className="flex-1 flex items-center justify-center">
            <h1
              style={{ direction: "rtl", textAlign: "center" }}
              className="leading-relaxed"
            >
              {q.text}
            </h1>
          </div>
        </div>
        <div className="h-4/6 xl:h-5/6 w-full flex justify-center items-start px-4 pb-4 xl:p-10">
          <Media q={q} />
        </div>

        {/* Multiple-choice overlay — same card the master's screen shows */}
        {active.multipleAnswers?.choices?.length > 0 && (
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-30">
            <div className="bg-white rounded-xl shadow-2xl border-4 border-behindButton w-[90vw] max-w-lg">
              <div className="bg-behindButton p-3 rounded-t-lg flex justify-center items-center">
                <h3
                  className="text-white font-bold text-lg text-center"
                  style={{ direction: "rtl" }}
                >
                  خيارات متعددة
                </h3>
              </div>
              <div className="p-4">
                <div
                  className="grid grid-cols-2 gap-3"
                  style={{ direction: "rtl" }}
                >
                  {active.multipleAnswers.choices.map((option, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg text-center font-semibold border-2 border-gray-200 relative overflow-hidden text-base"
                    >
                      <div className="absolute top-1 left-1 w-8 h-8 bg-behindButton rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {String.fromCharCode(65 + index)}
                      </div>
                      <div className="mt-2">{option}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* First-letter / answer-pattern cards — bottom-right like the master */}
        {(active.firstLetter?.letter || active.pattern) && (
          <div className="fixed bottom-16 right-4 z-30 flex flex-col gap-2">
            {active.pattern && (
              <div className="bg-white rounded-xl shadow-2xl border-4 border-mainButton w-64">
                <div className="bg-mainButton p-2 rounded-t-lg">
                  <h3
                    className="text-white font-bold text-sm text-center"
                    style={{ direction: "rtl" }}
                  >
                    نمط الإجابة
                  </h3>
                </div>
                <div className="p-3 text-center" style={{ direction: "rtl" }}>
                  <div className="text-xl font-mono tracking-widest text-dark">
                    {active.pattern.pattern}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {active.pattern.wordCount} كلمة • {active.pattern.charCount}{" "}
                    حرف
                  </div>
                </div>
              </div>
            )}
            {active.firstLetter?.letter && (
              <div className="bg-white rounded-xl shadow-2xl border-4 border-mainButton w-48">
                <div className="bg-mainButton p-2 rounded-t-lg">
                  <h3
                    className="text-white font-bold text-sm text-center"
                    style={{ direction: "rtl" }}
                  >
                    الحرف الأول
                  </h3>
                </div>
                <div className="p-3 flex justify-center">
                  <div className="w-12 h-12 bg-mainText text-header rounded-full flex items-center justify-center text-2xl font-bold">
                    {active.firstLetter.letter}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phone-a-friend indicator */}
        {active.phone && (
          <div className="absolute top-4 right-4 z-30 bg-behindButton text-white rounded-full px-4 py-1 text-sm flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            اتصال بصديق مُفعّل
          </div>
        )}

        {/* Reveal vote — same spot/style as the master's الإجابة button */}
        <div className="absolute bottom-0 transform translate-x-1/2 translate-y-1/2">
          <button
            onClick={() => voting.tap("question", "reveal")}
            className={`relative bg-mainButton hover:bg-mainButtonShadow text-white font-bold py-3 px-4 md:px-10 border-b-8 border-mainButtonShadow rounded-md text-sm md:text-xl ${voteTargetClasses(
              revealMine,
              voting.canVote
            )}`}
          >
            <VoteCount count={revealCount} />
            الإجابة
          </button>
        </div>
      </div>

      <TeamRail
        state={state}
        presence={presence}
        voting={voting}
        interactive
      />
    </div>
  );
};

export default LiveQuestion;
